/**
 * Haversine Distance Calculation Utilities
 * For calculating distances between geographic coordinates
 */

export interface ILatLng {
  lat: number;
  lng: number;
}

export interface ILocation {
  type: string;
  coordinates: number[]; // [lng, lat] - GeoJSON format
}

/**
 * Convert degrees to radians
 */
const toRadians = (degrees: number): number => degrees * (Math.PI / 180);

/**
 * Calculate the great-circle distance between two points using Haversine formula
 *
 * @param lat1 - Latitude of first point
 * @param lng1 - Longitude of first point
 * @param lat2 - Latitude of second point
 * @param lng2 - Longitude of second point
 * @returns Distance in kilometers
 */
export const calculateHaversineDistance = (
  lat1: number,
  lng1: number,
  lat2: number,
  lng2: number
): number => {
  const EARTH_RADIUS_KM = 6371;

  const dLat = toRadians(lat2 - lat1);
  const dLng = toRadians(lng2 - lng1);

  const a =
    Math.sin(dLat / 2) * Math.sin(dLat / 2) +
    Math.cos(toRadians(lat1)) *
      Math.cos(toRadians(lat2)) *
      Math.sin(dLng / 2) *
      Math.sin(dLng / 2);

  const c = 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a));

  return EARTH_RADIUS_KM * c;
};

/**
 * Calculate distance between two ILatLng points
 */
export const calculateDistanceBetweenPoints = (
  point1: ILatLng,
  point2: ILatLng
): number => {
  return calculateHaversineDistance(
    point1.lat,
    point1.lng,
    point2.lat,
    point2.lng
  );
};

/**
 * Calculate distance between two ILocation objects (GeoJSON format)
 */
export const calculateDistanceBetweenLocations = (
  location1: ILocation,
  location2: ILocation
): number => {
  return calculateHaversineDistance(
    location1.coordinates[1], // lat
    location1.coordinates[0], // lng
    location2.coordinates[1],
    location2.coordinates[0]
  );
};

/**
 * Convert ILocation (GeoJSON) to ILatLng
 */
export const locationToLatLng = (location: ILocation): ILatLng => ({
  lat: location.coordinates[1],
  lng: location.coordinates[0],
});

/**
 * Convert ILatLng to ILocation (GeoJSON)
 */
export const latLngToLocation = (latLng: ILatLng): ILocation => ({
  type: "Point",
  coordinates: [latLng.lng, latLng.lat] as number[],
});

/**
 * Calculate total distance of a route (array of points)
 */
export const calculateRouteDistance = (points: ILatLng[]): number => {
  if (points.length < 2) return 0;

  let totalDistance = 0;
  for (let i = 0; i < points.length - 1; i++) {
    totalDistance += calculateDistanceBetweenPoints(points[i], points[i + 1]);
  }

  return totalDistance;
};

/**
 * Calculate distances between each consecutive pair of points
 * Returns array of distances [dist(0-1), dist(1-2), ...]
 */
export const calculateSegmentDistances = (points: ILatLng[]): number[] => {
  if (points.length < 2) return [];

  const distances: number[] = [];
  for (let i = 0; i < points.length - 1; i++) {
    distances.push(calculateDistanceBetweenPoints(points[i], points[i + 1]));
  }

  return distances;
};

/**
 * Estimate travel time based on distance and average speed
 *
 * @param distanceKm - Distance in kilometers
 * @param avgSpeedKmh - Average speed in km/h (default: 30 for city delivery)
 * @returns Estimated time in minutes
 */
export const estimateTravelTime = (
  distanceKm: number,
  avgSpeedKmh: number = 30
): number => {
  return (distanceKm / avgSpeedKmh) * 60;
};

/**
 * Find the nearest point from a reference point
 */
export const findNearestPoint = (
  reference: ILatLng,
  points: ILatLng[]
): { point: ILatLng; index: number; distance: number } | null => {
  if (points.length === 0) return null;

  let nearestIndex = 0;
  let nearestDistance = calculateDistanceBetweenPoints(reference, points[0]);

  for (let i = 1; i < points.length; i++) {
    const distance = calculateDistanceBetweenPoints(reference, points[i]);
    if (distance < nearestDistance) {
      nearestDistance = distance;
      nearestIndex = i;
    }
  }

  return {
    point: points[nearestIndex],
    index: nearestIndex,
    distance: nearestDistance,
  };
};

/**
 * Sort points by distance from a reference point
 */
export const sortByDistance = <T extends { location: ILocation }>(
  reference: ILatLng,
  items: T[]
): Array<T & { distance: number }> => {
  return items
    .map((item) => ({
      ...item,
      distance: calculateDistanceBetweenPoints(
        reference,
        locationToLatLng(item.location)
      ),
    }))
    .sort((a, b) => a.distance - b.distance);
};

/**
 * Get bounds that contain all points (for map fitting)
 */
export const getBounds = (
  points: ILatLng[]
): { north: number; south: number; east: number; west: number } | null => {
  if (points.length === 0) return null;

  let north = points[0].lat;
  let south = points[0].lat;
  let east = points[0].lng;
  let west = points[0].lng;

  for (const point of points) {
    if (point.lat > north) north = point.lat;
    if (point.lat < south) south = point.lat;
    if (point.lng > east) east = point.lng;
    if (point.lng < west) west = point.lng;
  }

  return { north, south, east, west };
};

/**
 * Decode an encoded polyline string to an array of LatLng points
 * Uses Google Maps geometry encoding API if available
 */
export const decodePolyline = (encodedPath: string): ILatLng[] => {
  if (!encodedPath) return [];

  // Use Google Maps API if available
  if (typeof window !== 'undefined' && window.google?.maps?.geometry?.encoding) {
    try {
      const decodedPath = window.google.maps.geometry.encoding.decodePath(encodedPath);
      return decodedPath.map((latLng) => ({
        lat: latLng.lat(),
        lng: latLng.lng(),
      }));
    } catch (error) {
      console.error("Error decoding polyline:", error);
      return [];
    }
  }

  return [];
};

/**
 * Find the midpoint along a polyline path (not straight-line midpoint)
 * This calculates the point that is at 50% of the total path distance
 */
export const findPathMidpoint = (path: ILatLng[]): ILatLng | null => {
  if (!path || path.length === 0) return null;
  if (path.length === 1) return path[0];
  if (path.length === 2) {
    return {
      lat: (path[0].lat + path[1].lat) / 2,
      lng: (path[0].lng + path[1].lng) / 2,
    };
  }

  // Calculate total path distance
  let totalDistance = 0;
  const segmentDistances: number[] = [];

  for (let i = 0; i < path.length - 1; i++) {
    const dist = calculateDistanceBetweenPoints(path[i], path[i + 1]);
    segmentDistances.push(dist);
    totalDistance += dist;
  }

  if (totalDistance === 0) return path[0];

  // Find the point at 50% of total distance
  const targetDistance = totalDistance / 2;
  let accumulatedDistance = 0;

  for (let i = 0; i < segmentDistances.length; i++) {
    const segmentDist = segmentDistances[i];

    if (accumulatedDistance + segmentDist >= targetDistance) {
      // Midpoint is within this segment
      const remainingDistance = targetDistance - accumulatedDistance;
      const ratio = segmentDist > 0 ? remainingDistance / segmentDist : 0;

      const from = path[i];
      const to = path[i + 1];

      return {
        lat: from.lat + (to.lat - from.lat) * ratio,
        lng: from.lng + (to.lng - from.lng) * ratio,
      };
    }

    accumulatedDistance += segmentDist;
  }

  // Fallback to last point
  return path[path.length - 1];
};
